'use client';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { getEnv } from './side-env.js';
import { getSharedSegmentKey } from './segment-keys.js';
const analytics = new AnalyticsBrowser();
let libConfig;
/**
 *
 * @param config - Analytics config
 */
export function initialize(config) {
    libConfig = config;
    const env = getEnv(config.env);
    // When running in Cypress environment always set an anonymous id to prevent
    // unnecessary usage of Segment MTU
    if (typeof window !== 'undefined' &&
        window.Cypress) {
        const windowWithCypress = window;
        const testUid = windowWithCypress.Cypress.env?.('TEST_UID');
        if (testUid) {
            console.log(`[@side/analytics] TEST_UID set within Cypress environment, identifying: ${JSON.stringify({ userId: testUid })}`);
            // TODO: [PLAT-7671] Fix ESLint TypeScript disable ignores in analytics library
            // This seems like we should be awaiting on the promise but going to hold
            // off until PLAT-7671.
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            analytics.identify(testUid);
        }
        else {
            console.log('[@side/analytics] Anonymous id set for test user');
            // TODO: [PLAT-7671] Fix ESLint TypeScript disable ignores in analytics library
            // This seems like we should be awaiting on the promise but going to hold
            // off until PLAT-7671.
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            analytics.setAnonymousId('TEST_USER_ANONYMOUS_ID');
        }
    }
    // Enable debug mode if specified by config
    if (config.debug) {
        analytics.debug(true);
    }
    // Load segment analytics library with key
    analytics.load({
        writeKey: getSharedSegmentKey(env),
    });
}
export const { setAnonymousId, debug } = analytics;
/**
 * Track analytics event
 * @param eventName - Event name
 * @param payload - Event payload
 * @param options - Options
 * @returns Promise which resolves after capturing analytic
 * @example
 * import { track } from '@side/analytics'
 * track({ eventTarget: 'save_button', eventType: 'click' })
 */
export function track(eventName, payload, options) {
    return analytics.track(eventName, { ...payload, sideApp: libConfig.app }, options);
}
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Track page view
 *
 * TODO: [PLAT-4729] Breaking change in analytics page to match Segment's options and add sideApp
 * @param data - Page data
 * @param options - Options
 * @returns Promise which resolves after capturing page analytic
 */
export function page(data, options) {
    // TODO: look into other arguments of page - should we use pageView instead?
    // TODO: [PLAT-7671] Fix ESLint TypeScript disable ignores in analytics library
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return analytics.page(data, options);
}
/**
 * Identify user within Side analytics library.
 * @param userId - User's UID
 * @param traits - User traits
 * @returns Promise which resolves after identifying
 */
export function identify(userId, traits) {
    // TODO: [PLAT-7671] Fix ESLint TypeScript disable ignores in analytics library
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const identifyTraits = {
        version: process.env.NEXT_PUBLIC_APP_VERSION,
        ...traits,
    };
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return analytics.identify(userId, identifyTraits);
}
/* eslint-enable @typescript-eslint/no-explicit-any */
/**
 * Track a route change
 * NOTE: page promise not returned to prevent misused promises warning
 * from lint when attaching a route listener
 * @param url - Page url
 * @example
 * import { routeChangeHandler } from '@side/analytics'
 * function App({ router }) {
 *   useEffect(() => {
 *     router.events.on('routeChangeComplete', routeChangeHandler);
 *     return () => {
 *       router.events.off('routeChangeComplete', routeChangeHandler);
 *     };
 *   }, [router]);
 *  return <RestOfApp />
 * }
 */
export function routeChangeHandler(url) {
    // TODO: [PLAT-7671] Fix ESLint TypeScript disable ignores in analytics library
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    page({ url });
}
